import { forwardRef, useState } from "react";

import { CircleInformation } from "@/assets/icons";
import type { ModuleIntroConfig } from "@/components/onboarding/configs/getModuleConfig";
import { ProductOverviewPanel } from "@/components/onboarding/ProductOverviewPanel";
import { OnboardingIntroDialog } from "@/components/onboarding/ProductOverviewPanel/OnboardingIntroDialog";
import Tooltip from "@/components/ui/tooltip";
import { useEnterOnboardingMode } from "@/hooks/onboarding/useOnboarding";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { SidebarFooterPopoverType } from "../types";
import FooterItemWithPopover from "./FooterItemWithPopover";

type OnboardingMenuProps = {
  setActivePopover: (popoverType: SidebarFooterPopoverType) => void;
  activePopover?: SidebarFooterPopoverType;
  isPopoverTriggerOrigin: (e: Event) => boolean;
  sidebarMenu?: boolean;
};

type ActiveIntro = {
  moduleCode: string;
  intro: ModuleIntroConfig;
};

export const OnboardingMenu = forwardRef<
  HTMLButtonElement,
  OnboardingMenuProps
>(
  (
    { activePopover, isPopoverTriggerOrigin, setActivePopover, sidebarMenu },
    ref
  ) => {
    const isActive = activePopover === "onboarding";
    const enterOnboardingMode = useEnterOnboardingMode();
    const [activeIntro, setActiveIntro] = useState<ActiveIntro | null>(null);

    const handleStart = (code: string, intro?: ModuleIntroConfig) => {
      if (intro) {
        setActiveIntro({ moduleCode: code, intro });
        return;
      }

      enterOnboardingMode(code);
    };

    const handleIntroOpenChange = (open: boolean) => {
      if (!open) setActiveIntro(null);
    };

    const handleIntroStart = () => {
      if (!activeIntro) return;

      const { moduleCode } = activeIntro;
      setActiveIntro(null);
      enterOnboardingMode(moduleCode);
    };

    return (
      <>
        <FooterItemWithPopover
          togglePopover={(open) => {
            setActivePopover(open ? "onboarding" : "");
          }}
          classNamePopoverContent={twMerge(
            "mt-3 w-[420px] max-w-[420px]",
            sidebarMenu && "ml-6"
          )}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          popoverTrigger={
            <Button
              variant="secondary"
              aria-label="Product Overview"
              data-testid="onboarding-popover-trigger"
              ref={ref}
              className={twMerge(
                "!p-0 rounded-lg hover:bg-teal-50 border-0",
                isActive && "bg-teal-50",
                sidebarMenu
                  ? "w-[231px] justify-start"
                  : "h-10 w-10 justify-center"
              )}
            >
              <Tooltip
                disable={sidebarMenu}
                content="Product Overview"
                position="right"
                key="Product Overview"
              >
                <div className="flex items-center justify-center">
                  <CircleInformation
                    viewBox="-1 -1 22 22"
                    className={twMerge(
                      "fill-dim-gray hover:fill-teal-500 w-10 h-10 shrink-0 p-2",
                      isActive && "fill-teal-500"
                    )}
                  />
                  <span
                    className={twMerge(
                      "whitespace-pre text-sm font-medium",
                      !sidebarMenu &&
                        "opacity-0 translate-x-28 overflow-hidden hidden",
                      isActive ? "text-black font-bold" : "text-dim-gray"
                    )}
                  >
                    Product Overview
                  </span>
                </div>
              </Tooltip>
            </Button>
          }
          popoverContent={
            <ProductOverviewPanel
              onClose={() => setActivePopover("")}
              onStart={handleStart}
            />
          }
          isOpen={isActive}
        />
        {activeIntro && (
          <OnboardingIntroDialog
            intro={activeIntro.intro}
            open
            onOpenChange={handleIntroOpenChange}
            onStart={handleIntroStart}
          />
        )}
      </>
    );
  }
);

OnboardingMenu.displayName = "OnboardingMenu";

export default OnboardingMenu;
